import './style.css';
import { FIXED_DT, MAX_STEPS_PER_FRAME } from './game/constants';
import { GameEngine } from './game/GameEngine';
import { CanvasGameView } from './game/CanvasGameView';
import { getLocale, setLocale, t, translateDocument, type Locale } from './i18n';
import { AudioSystem } from './systems/audio';
import { InputManager, type GamepadSlot, type GamepadTarget, type InputBindings, type KeyboardMoveScheme, type TouchTarget } from './systems/input';
import { HudController } from './ui/hud';
import { TouchControlsController, type TouchControlMode } from './ui/touchControls';
import type { BossVariant, EnemyAiMode, GameModeConfig, ObstacleDensity, PlayerCount, PlayerNumber, PlayerTankClass, PowerupKind, UpgradeDropRate } from './game/types';
import { loadPlayerProfile, sanitizeNickname, savePlayerProfile } from './leaderboard/profile';
import { DEFAULT_RANKED_MODE, RANKED_MODES, rankedModeForConfig, type RankedModeCode } from './leaderboard/ranked';

const root = required('game-root');
const overlay = required('overlay');
const toast = required('toast');
const rankedCelebration = required('ranked-celebration');
const rankedCelebrationTitle = required('ranked-celebration-title');
const rankedCelebrationSummary = required('ranked-celebration-summary');
const rankedCelebrationBadges = required('ranked-celebration-badges');
const startButton = requiredButton('btn-start');
const resumeButton = requiredButton('btn-resume');
const restartButton = requiredButton('btn-restart');
const applyButton = requiredButton('btn-apply');
const menuButton = requiredButton('btn-menu');
const continueButton = requiredButton('btn-continue');
const abandonButton = requiredButton('btn-abandon');
const fullscreenButton = requiredButton('btn-fullscreen');
const muteButton = requiredButton('btn-mute');
const sfxTestButton = requiredButton('btn-sfx-test');
const guideButton = requiredButton('btn-guide');
const guideOverlay = required('guide-overlay');
const guideCloseButton = requiredButton('guide-close');
const leaderboardOpenButton = requiredButton('btn-leaderboard');
const leaderboardProfileButton = requiredButton('btn-leaderboard-profile');
const leaderboardOverlay = required('leaderboard-overlay');
const leaderboardCloseButton = requiredButton('leaderboard-close');
const rankedIdentityNickname = required('ranked-identity-nickname');
const rankedIdentityBoard = required('ranked-identity-board');
const rankedIdentityStandingTitle = required('ranked-identity-standing-title');
const rankedIdentityStandingDetail = required('ranked-identity-standing-detail');
const rulesetSelect = requiredSelect('mode-ruleset');
const rankedPresetSelect = requiredSelect('mode-ranked-preset');
const playerCountSelect = requiredSelect('mode-player-count');
const p1TankClassSelect = requiredSelect('mode-p1-tank-class');
const p2TankClassSelect = requiredSelect('mode-p2-tank-class');
const p3TankClassSelect = requiredSelect('mode-p3-tank-class');
const p4TankClassSelect = requiredSelect('mode-p4-tank-class');
const localeSelect = requiredSelect('mode-locale');
const infiniteToggle = requiredInput('mode-infinite');
const baseToggle = requiredInput('mode-base');
const friendlyToggle = requiredInput('mode-friendly');
const upgradeRateSelect = requiredSelect('mode-upgrade-rate');
const loseUpgradeToggle = requiredInput('mode-lose-upgrade');
const levelDelaySelect = requiredSelect('mode-level-delay');
const obstacleDensitySelect = requiredSelect('mode-obstacle-density');
const enemyAiSelect = requiredSelect('mode-enemy-ai');
const initialLevelSelect = requiredSelect('mode-initial-level');
const p1FireBindButton = requiredButton('bind-p1-fire');
const p2FireBindButton = requiredButton('bind-p2-fire');
const p3FireBindButton = requiredButton('bind-p3-fire');
const p4FireBindButton = requiredButton('bind-p4-fire');
const p1MoveSchemeSelect = requiredSelect('mode-p1-move-scheme');
const p2MoveSchemeSelect = requiredSelect('mode-p2-move-scheme');
const p3MoveSchemeSelect = requiredSelect('mode-p3-move-scheme');
const p4MoveSchemeSelect = requiredSelect('mode-p4-move-scheme');
const p1GamepadSlotSelect = requiredSelect('mode-p1-gamepad-slot');
const p2GamepadSlotSelect = requiredSelect('mode-p2-gamepad-slot');
const p3GamepadSlotSelect = requiredSelect('mode-p3-gamepad-slot');
const p4GamepadSlotSelect = requiredSelect('mode-p4-gamepad-slot');
const p1Group = required('player-group-p1');
const p2Group = required('player-group-p2');
const p3Group = required('player-group-p3');
const p4Group = required('player-group-p4');
const localMultiplayerNote = required('local-multiplayer-note');
const p1GroupSummary = required('player-group-p1-summary');
const p2GroupSummary = required('player-group-p2-summary');
const p3GroupSummary = required('player-group-p3-summary');
const p4GroupSummary = required('player-group-p4-summary');
const p1ControlHint = required('mode-p1-control-hint');
const p2ControlHint = required('mode-p2-control-hint');
const p3ControlHint = required('mode-p3-control-hint');
const p4ControlHint = required('mode-p4-control-hint');
const p1MoveHint = required('mode-p1-move-hint');
const p2MoveHint = required('mode-p2-move-hint');
const p3MoveHint = required('mode-p3-move-hint');
const p4MoveHint = required('mode-p4-move-hint');
const p1FireHint = required('mode-p1-fire-hint');
const p2FireHint = required('mode-p2-fire-hint');
const p3FireHint = required('mode-p3-fire-hint');
const p4FireHint = required('mode-p4-fire-hint');
const p1MoveSchemeRow = required('row-p1-move-scheme');
const p2MoveSchemeRow = required('row-p2-move-scheme');
const p3MoveSchemeRow = required('row-p3-move-scheme');
const p4MoveSchemeRow = required('row-p4-move-scheme');
const p1GamepadSlotRow = required('row-p1-gamepad-slot');
const p2GamepadSlotRow = required('row-p2-gamepad-slot');
const p3GamepadSlotRow = required('row-p3-gamepad-slot');
const p4GamepadSlotRow = required('row-p4-gamepad-slot');
const p1FireBindRow = required('row-p1-fire-bind');
const p2FireBindRow = required('row-p2-fire-bind');
const p3FireBindRow = required('row-p3-fire-bind');
const p4FireBindRow = required('row-p4-fire-bind');
const p2TankClassRow = required('row-p2-tank-class');
const p3TankClassRow = required('row-p3-tank-class');
const p4TankClassRow = required('row-p4-tank-class');
const p1SourceLabel = required('control-p1-source');
const p2SourceLabel = required('control-p2-source');
const p3SourceLabel = required('control-p3-source');
const p4SourceLabel = required('control-p4-source');
const p1FireLabel = required('control-p1-fire');
const p2FireLabel = required('control-p2-fire');
const p3FireLabel = required('control-p3-fire');
const p4FireLabel = required('control-p4-fire');
const p3ControlRow = required('control-p3-row');
const p4ControlRow = required('control-p4-row');
const p1ControlSelect = requiredSelect('mode-p1-control');
const p2ControlSelect = requiredSelect('mode-p2-control');
const p3ControlSelect = requiredSelect('mode-p3-control');
const p4ControlSelect = requiredSelect('mode-p4-control');
const touchControlsRoot = required('touch-controls');
const rankedSection = required('ranked-section');
const customBasicSection = required('custom-basic-section');
const customRulesSection = required('custom-rules-section');
const customProgressionSection = required('custom-progression-section');
const rankedSummaryTitle = required('ranked-summary-title');
const rankedSummarySubtitle = required('ranked-summary-subtitle');
const rankedSummaryList = required('ranked-summary-list');
const leaderboardEligibility = required('leaderboard-eligibility');
const leaderboardRefreshButton = requiredButton('leaderboard-refresh');
const leaderboardNicknameInput = requiredInput('leaderboard-nickname');
const leaderboardTabs = Array.from(document.querySelectorAll<HTMLButtonElement>('.leaderboard-tab'));
const leaderboardStatus = required('leaderboard-status');
const leaderboardStatusBadges = required('leaderboard-status-badges');
const leaderboardPersonal = required('leaderboard-personal');
const leaderboardList = required('leaderboard-list');
const devTools = required('dev-tools');
const devToolsBody = required('dev-tools-body');
const devToolsToggleButton = requiredButton('dev-tools-toggle');
const devLevelInput = requiredInput('dev-level-input');
const devJumpButton = requiredButton('dev-jump-level');
const devPowerupSelect = requiredSelect('dev-powerup-select');
const devEnemySelect = requiredSelect('dev-enemy-select');
const devBossVariantSelect = requiredSelect('dev-boss-variant-select');
const devTimeScaleSelect = requiredSelect('dev-time-scale');
const devAiOverlayToggle = requiredInput('dev-ai-overlay');
const devPlayerInvincibleToggle = requiredInput('dev-player-invincible');
const devBaseInvincibleToggle = requiredInput('dev-base-invincible');
const devAudioState = required('dev-audio-state');
const devWakeAudioButton = requiredButton('dev-wake-audio');
const devSpawnPowerupButton = requiredButton('dev-spawn-powerup');
const devSpawnEnemyButton = requiredButton('dev-spawn-enemy');
const devStepFrameButton = requiredButton('dev-step-frame');
const devHealButton = requiredButton('dev-heal');
const devUpgradeButton = requiredButton('dev-upgrade');
const devClearEnemiesButton = requiredButton('dev-clear-enemies');
const devSpawnBossButton = requiredButton('dev-spawn-boss');
const devDamageBaseButton = requiredButton('dev-damage-base');

const engine = new GameEngine();
const view = new CanvasGameView();
const input = new InputManager(window);
const audio = new AudioSystem();
const hud = new HudController();
const touchCapable = isTouchCapable();
const touchControls = new TouchControlsController({
  root: touchControlsRoot,
  onChange: (controls) => input.setTouchControls(controls),
  onModeChange: (mode) => {
    syncTouchModeSelection(mode);
    saveControlBindings();
    applyInputSettings();
  },
  onPause: () => {
    if (engine.status === 'menu' || engine.status === 'gameOver') return;
    if (overlayMode === 'pause' || engine.status === 'paused') resumeGame();
    else showPauseMenu();
  },
});

let accumulator = 0;
let toastTimer = 0;
let rankedCelebrationTimer = 0;
let overlayMode: 'main' | 'pause' | 'gameOver' | 'continue' = 'main';
let fireBindings = loadControlBindings();
let bindingTarget: PlayerNumber | null = null;
let softFullscreen = false;
let developerMenuVisible = false;
let developerMenuResumeOnClose = false;
let developerMenuReturnToPause = false;
let developerToolsCollapsed = false;
let developerTimeScale = 1;
let developerShowAiOverlay = false;
const LOCALE_STORAGE_KEY = 'siege-tank-locale';
const BUILD_VERSION = `web-${import.meta.env.MODE ?? 'dev'}`;
const KEYBOARD_MOVE_SCHEMES: KeyboardMoveScheme[] = ['arrows', 'wasd', 'tfgh', 'ijkl', 'numpad'];
let leaderboardMode: RankedModeCode = DEFAULT_RANKED_MODE;
let leaderboardState: LeaderboardViewState = { status: 'loading', entries: [], myEntry: null };
let playerProfile = loadPlayerProfile();
let runStartedAt = 0;
let runContinueUsed = false;
let runDebugInvalidated = false;
let leaderboardSubmissionAttempted = false;
let pendingRankedSnapshot: RankedRunSnapshot | null = null;
let latestRankedResult: RankedSubmissionFeedback | null = null;

applyStoredLocale();
document.body.classList.toggle('touch-capable', touchCapable);
if (touchCapable) installMobileGestureGuards();
installAudioWakeGuards();
leaderboardNicknameInput.value = playerProfile.nickname;
syncRulesetUi(false);
renderControlBindings();
applyInputSettings();
document.body.classList.add('overlay-open');

await view.init(root);
view.setDebugOptions({ showAiDebug: developerShowAiOverlay });
applyLocale();
view.render(engine);
hud.update(engine);
refreshLeaderboardEligibility();
void loadLeaderboard();

startButton.addEventListener('click', () => {
  audio.resume();
  startWithCurrentSettings();
});

resumeButton.addEventListener('click', resumeGame);

restartButton.addEventListener('click', () => {
  if (!confirmBattleExit(t('confirm.restartLevel'))) return;
  audio.resume();
  engine.restartLevel();
  hideOverlay();
});

applyButton.addEventListener('click', () => {
  if (!confirmBattleExit(t('confirm.applySettings'))) return;
  audio.resume();
  startWithCurrentSettings();
});

menuButton.addEventListener('click', () => {
  if (!confirmBattleExit(t('confirm.returnMenu'))) return;
  engine.preparePreview();
  engine.status = 'menu';
  showOverlay('main');
});

continueButton.addEventListener('click', () => {
  audio.resume();
  void continueRankedPractice();
});

abandonButton.addEventListener('click', async () => {
  if (!window.confirm(t('confirm.abandonContinue'))) return;
  await maybeSubmitLeaderboard('abandon');
  engine.abandonRun();
  showOverlay('main');
});

fullscreenButton.addEventListener('click', () => void toggleFullscreenMode());
document.addEventListener('fullscreenchange', syncFullscreenUi);
document.addEventListener('fullscreenerror', () => {
  setSoftFullscreen(true);
  showToast(t('app.immersive'));
});
window.addEventListener('orientationchange', () => {
  if (softFullscreen) window.setTimeout(() => window.scrollTo(0, 1), 120);
});

muteButton.addEventListener('click', () => {
  audio.muted = !audio.muted;
  muteButton.textContent = audio.muted ? '×' : '♪';
  refreshAudioDebugUi();
});

sfxTestButton.addEventListener('click', () => {
  void audio.resume().finally(() => refreshAudioDebugUi());
  audio.previewExplosion();
});

guideButton.addEventListener('click', () => openGameplayGuide());
guideCloseButton.addEventListener('click', () => closeGameplayGuide());
guideOverlay.addEventListener('click', (event) => {
  if (event.target === guideOverlay) closeGameplayGuide();
});
leaderboardOpenButton.addEventListener('click', () => openLeaderboardDialog());
leaderboardProfileButton.addEventListener('click', () => openLeaderboardDialog(true));
leaderboardCloseButton.addEventListener('click', () => closeLeaderboardDialog());
leaderboardOverlay.addEventListener('click', (event) => {
  if (event.target === leaderboardOverlay) closeLeaderboardDialog();
});

devWakeAudioButton.addEventListener('click', () => {
  void audio.resume().finally(() => {
    refreshAudioDebugUi();
    showToast(`${t('dev.audioState')} · ${audioStateLabel()}`);
  });
});

devJumpButton.addEventListener('click', () => {
  const targetLevel = parseDebugLevel(devLevelInput.value);
  audio.resume();
  engine.debugJumpToLevel(targetLevel);
  showToast(t('dev.toast.jump', { level: targetLevel }));
  hideDeveloperMenuAfterJump();
});

devSpawnPowerupButton.addEventListener('click', () => {
  audio.resume();
  engine.debugSpawnPowerup(devPowerupSelect.value as PowerupKind);
});

devSpawnEnemyButton.addEventListener('click', () => {
  audio.resume();
  engine.debugSpawnEnemy(devEnemySelect.value as 'light' | 'assault' | 'heavy' | 'sniper');
});

devTimeScaleSelect.addEventListener('change', () => {
  developerTimeScale = parseDebugTimeScale(devTimeScaleSelect.value);
  showToast(t('dev.toast.speed', { scale: developerTimeScale.toFixed(developerTimeScale < 1 ? 2 : 1).replace(/\.0$/, '') }));
});

devAiOverlayToggle.addEventListener('change', () => {
  developerShowAiOverlay = devAiOverlayToggle.checked;
  view.setDebugOptions({ showAiDebug: developerShowAiOverlay });
});

devPlayerInvincibleToggle.addEventListener('change', () => {
  audio.resume();
  engine.debugSetPlayerInvincible(devPlayerInvincibleToggle.checked);
});

devBaseInvincibleToggle.addEventListener('change', () => {
  audio.resume();
  engine.debugSetBaseInvincible(devBaseInvincibleToggle.checked);
});

devStepFrameButton.addEventListener('click', () => {
  audio.resume();
  const snapshot = input.snapshot();
  snapshot.pausePressed = false;
  engine.debugAdvanceFrame(FIXED_DT, snapshot);
  processEngineEvents();
  view.render(engine);
  hud.update(engine);
});

devHealButton.addEventListener('click', () => {
  audio.resume();
  engine.debugHealPlayers();
});

devUpgradeButton.addEventListener('click', () => {
  audio.resume();
  engine.debugSetPlayerUpgradeLevel(4);
});

devClearEnemiesButton.addEventListener('click', () => {
  audio.resume();
  engine.debugClearEnemies();
});

devSpawnBossButton.addEventListener('click', () => {
  audio.resume();
  engine.debugSpawnEnemyVariant('boss', devBossVariantSelect.value as BossVariant);
});

devDamageBaseButton.addEventListener('click', () => {
  audio.resume();
  engine.debugDamageBase();
});

devToolsToggleButton.addEventListener('click', () => {
  developerToolsCollapsed = !developerToolsCollapsed;
  syncDeveloperMenuUi();
});

rulesetSelect.addEventListener('change', () => {
  syncRulesetUi();
  applyInputSettings();
});
rankedPresetSelect.addEventListener('change', () => {
  syncRulesetUi();
  applyInputSettings();
});
playerCountSelect.addEventListener('change', () => {
  normalizeControlSettings();
  saveControlBindings();
  applyInputSettings();
});
p1TankClassSelect.addEventListener('change', () => refreshLeaderboardEligibility());
p2TankClassSelect.addEventListener('change', () => refreshLeaderboardEligibility());
p3TankClassSelect.addEventListener('change', () => refreshLeaderboardEligibility());
p4TankClassSelect.addEventListener('change', () => refreshLeaderboardEligibility());
p1ControlSelect.addEventListener('change', () => updatePlayerControl(1));
p2ControlSelect.addEventListener('change', () => updatePlayerControl(2));
p3ControlSelect.addEventListener('change', () => updatePlayerControl(3));
p4ControlSelect.addEventListener('change', () => updatePlayerControl(4));
p1MoveSchemeSelect.addEventListener('change', () => updateKeyboardMoveScheme(1));
p2MoveSchemeSelect.addEventListener('change', () => updateKeyboardMoveScheme(2));
p3MoveSchemeSelect.addEventListener('change', () => updateKeyboardMoveScheme(3));
p4MoveSchemeSelect.addEventListener('change', () => updateKeyboardMoveScheme(4));
p1GamepadSlotSelect.addEventListener('change', () => updateGamepadSlot(1));
p2GamepadSlotSelect.addEventListener('change', () => updateGamepadSlot(2));
p3GamepadSlotSelect.addEventListener('change', () => updateGamepadSlot(3));
p4GamepadSlotSelect.addEventListener('change', () => updateGamepadSlot(4));

p1FireBindButton.addEventListener('click', () => beginFireBinding(1));
p2FireBindButton.addEventListener('click', () => beginFireBinding(2));
p3FireBindButton.addEventListener('click', () => beginFireBinding(3));
p4FireBindButton.addEventListener('click', () => beginFireBinding(4));
leaderboardRefreshButton.addEventListener('click', () => {
  void loadLeaderboard(true);
});
leaderboardTabs.forEach((tab) => {
  tab.addEventListener('click', () => {
    setLeaderboardMode(tab.dataset.mode as RankedModeCode);
  });
});
leaderboardNicknameInput.addEventListener('change', persistLeaderboardNickname);
leaderboardNicknameInput.addEventListener('blur', persistLeaderboardNickname);
localeSelect.addEventListener('change', () => {
  applyLocaleSelection(localeSelect.value === 'en' ? 'en' : 'zh');
});

window.addEventListener('keydown', (event) => {
  if (isLeaderboardDialogOpen()) {
    if (event.code === 'Escape') {
      event.preventDefault();
      closeLeaderboardDialog();
    }
    return;
  }
  if (isGameplayGuideOpen()) {
    if (event.code === 'Escape') {
      event.preventDefault();
      closeGameplayGuide();
    }
    return;
  }
  if (bindingTarget) {
    event.preventDefault();
    if (event.code !== 'Escape') {
      setFireBinding(bindingTarget, event.code, labelForKeyEvent(event));
    }
    endFireBinding();
    return;
  }
  if (isEditableTarget(event.target)) return;
  if (isDeveloperShortcut(event)) {
    if (engine.status === 'menu' || engine.status === 'gameOver') return;
    event.preventDefault();
    toggleDeveloperMenu();
    return;
  }
  if (event.code === 'Escape' && engine.status !== 'menu' && engine.status !== 'gameOver') {
    if (developerMenuVisible) {
      event.preventDefault();
      closeDeveloperMenu();
      return;
    }
    event.preventDefault();
    if (overlayMode === 'pause' || engine.status === 'paused') resumeGame();
    else showPauseMenu();
    return;
  }
  if (event.code === 'KeyR' && !input.isFireCode(event.code) && engine.status !== 'menu') {
    if (!confirmBattleExit(t('confirm.restartLevel'))) return;
    audio.resume();
    engine.restartLevel();
    hideOverlay();
  }
});

view.app.ticker.add((ticker) => {
  const frameSeconds = Math.min(0.1, ticker.deltaMS / 1000) * developerTimeScale;
  accumulator += frameSeconds;
  let steps = 0;
  while (accumulator >= FIXED_DT && steps < MAX_STEPS_PER_FRAME) {
    const snapshot = input.snapshot();
    snapshot.pausePressed = false;
    engine.update(FIXED_DT, snapshot);
    accumulator -= FIXED_DT;
    steps += 1;
  }
  if (steps === MAX_STEPS_PER_FRAME) accumulator = 0;

  processEngineEvents();

  toastTimer = Math.max(0, toastTimer - frameSeconds);
  if (engine.status === 'levelComplete') toastTimer = 0;
  if (toastTimer <= 0) toast.classList.remove('show');
  rankedCelebrationTimer = Math.max(0, rankedCelebrationTimer - frameSeconds);
  if (rankedCelebrationTimer <= 0) hideRankedCelebration();
  view.render(engine);
  hud.update(engine);
});

function processEngineEvents(): void {
  for (const event of engine.drainEvents()) {
    audio.handle(event);
    if (event.kind === 'message' && event.text && shouldShowToast(event.text)) showToast(event.text);
    if (event.kind === 'shake') view.addShake(event.amount ?? 6);
    if (event.kind === 'gameOver') {
      void showGameOver();
    }
  }
}

function shouldShowToast(text: string): boolean {
  if (engine.status !== 'levelComplete') return true;
  return text !== t('message.resume') && text !== t('message.levelComplete');
}

function showToast(text: string): void {
  toast.textContent = text;
  toast.classList.add('show');
  toastTimer = 1.45;
}

function showRankedCelebration(result: RankedSubmissionFeedback): void {
  const titleKey = celebrationTitleKeyForResult(result);
  if (!titleKey) {
    hideRankedCelebration();
    return;
  }

  const milestones = milestoneLabelsForResult(result);
  const tier = celebrationTierForResult(result);
  rankedCelebration.dataset.tier = tier;
  rankedCelebrationTitle.textContent = t(titleKey);
  rankedCelebrationSummary.textContent = t('leaderboard.celebrationSummary', {
    mode: t(RANKED_MODES[result.modeCode].labelKey),
    score: result.score,
    rank: result.rank,
  });
  rankedCelebrationBadges.replaceChildren();
  milestones.forEach(({ key, emphasis }) => {
    const badge = document.createElement('span');
    badge.className = `leaderboard-status-badge${emphasis ? ' emphasis' : ''}`;
    badge.textContent = t(key);
    rankedCelebrationBadges.appendChild(badge);
  });
  rankedCelebration.classList.remove('hidden');
  rankedCelebration.classList.add('show');
  rankedCelebrationTimer = 3.6;
  audio.playRankedCelebration(tier);
}

function hideRankedCelebration(): void {
  rankedCelebration.classList.remove('show');
  rankedCelebration.classList.add('hidden');
  rankedCelebrationTimer = 0;
}

async function toggleFullscreenMode(): Promise<void> {
  const shell = document.getElementById('app-shell') ?? document.body;
  if (document.fullscreenElement) {
    softFullscreen = false;
    await document.exitFullscreen?.();
    syncFullscreenUi();
    return;
  }
  if (softFullscreen) {
    setSoftFullscreen(false);
    return;
  }
  if (document.fullscreenEnabled && shell.requestFullscreen) {
    try {
      await shell.requestFullscreen({ navigationUI: 'hide' });
      softFullscreen = false;
      syncFullscreenUi();
      return;
    } catch {
      // iOS Safari/Chrome often reject fullscreen for non-video pages; fall through to soft fullscreen.
    }
  }
  setSoftFullscreen(true);
  showToast(t('app.immersive'));
}

function setSoftFullscreen(enabled: boolean): void {
  softFullscreen = enabled;
  document.body.classList.toggle('is-soft-fullscreen', enabled);
  syncFullscreenUi();
  if (enabled) {
    window.setTimeout(() => window.scrollTo(0, 1), 60);
  }
}

function syncFullscreenUi(): void {
  const active = Boolean(document.fullscreenElement) || softFullscreen;
  document.body.classList.toggle('is-fullscreen', active);
  fullscreenButton.classList.toggle('active', active);
  fullscreenButton.title = active ? t('action.exitFullscreen') : t('action.fullscreen');
}

async function showGameOver(): Promise<void> {
  const reason = engine.getGameOverReasonText();
  showToast(reason);
  if (engine.continuesRemaining > 0) {
    await maybeSubmitLeaderboard('continueCheckpoint');
    showOverlay('continue');
  } else {
    showOverlay('gameOver');
    await maybeSubmitLeaderboard('gameOver');
  }
}

function startWithCurrentSettings(): void {
  const mode = currentConfiguredMode();
  applyInputSettings();
  engine.startNewGame(mode);
  runStartedAt = Date.now();
  runContinueUsed = false;
  runDebugInvalidated = false;
  leaderboardSubmissionAttempted = false;
  pendingRankedSnapshot = null;
  latestRankedResult = null;
  hideRankedCelebration();
  refreshLeaderboardEligibility();
  hideOverlay();
}

function showPauseMenu(): void {
  engine.togglePause();
  showOverlay('pause');
}

function resumeGame(): void {
  if (engine.status === 'paused') engine.togglePause();
  hideOverlay();
}

function confirmBattleExit(message: string): boolean {
  if (engine.status === 'menu' || engine.status === 'gameOver') return true;
  return window.confirm(message);
}

function openGameplayGuide(): void {
  guideOverlay.classList.remove('hidden');
  document.body.classList.add('guide-open');
  guideCloseButton.focus({ preventScroll: true });
}

function closeGameplayGuide(): void {
  guideOverlay.classList.add('hidden');
  document.body.classList.remove('guide-open');
  if (overlay.classList.contains('active')) guideButton.focus({ preventScroll: true });
}

function isGameplayGuideOpen(): boolean {
  return !guideOverlay.classList.contains('hidden');
}

function openLeaderboardDialog(focusNickname = false): void {
  leaderboardOverlay.classList.remove('hidden');
  document.body.classList.add('guide-open');
  if (isRankedRuleset() && leaderboardMode !== selectedRankedMode()) {
    setLeaderboardMode(selectedRankedMode(), false);
  } else {
    void loadLeaderboard();
  }
  window.requestAnimationFrame(() => {
    (focusNickname ? leaderboardNicknameInput : leaderboardCloseButton).focus({ preventScroll: true });
    if (focusNickname) leaderboardNicknameInput.select();
  });
}

function closeLeaderboardDialog(): void {
  persistLeaderboardNickname();
  leaderboardOverlay.classList.add('hidden');
  document.body.classList.remove('guide-open');
  if (overlay.classList.contains('active')) leaderboardOpenButton.focus({ preventScroll: true });
}

function isLeaderboardDialogOpen(): boolean {
  return !leaderboardOverlay.classList.contains('hidden');
}

function hideOverlay(): void {
  developerMenuVisible = false;
  developerMenuResumeOnClose = false;
  developerMenuReturnToPause = false;
  overlay.classList.remove('active');
  document.body.classList.remove('overlay-open', 'overlay-pause');
  overlayMode = 'main';
  syncDeveloperMenuUi();
}

function showOverlay(mode: 'main' | 'pause' | 'gameOver' | 'continue'): void {
  if (mode !== 'pause') {
    developerMenuVisible = false;
    developerMenuResumeOnClose = false;
    developerMenuReturnToPause = false;
  }
  overlayMode = mode;
  overlay.classList.add('active');
  document.body.classList.add('overlay-open');
  document.body.classList.toggle('overlay-pause', mode === 'pause');
  overlay.dataset.mode = mode;
  refreshOverlayCopy();
  syncDeveloperMenuUi();
}

function refreshOverlayCopy(): void {
  const title = overlay.querySelector('h1');
  const subtitle = overlay.querySelector('.subtitle');
  const copy = overlayCopyForMode(overlayMode);
  if (title) title.textContent = copy.title;
  if (subtitle) subtitle.textContent = copy.subtitle;
  startButton.classList.toggle('hidden', overlayMode !== 'main' && overlayMode !== 'gameOver');
  startButton.textContent = overlayMode === 'gameOver' ? t('action.redeploy') : t('action.start');
  resumeButton.classList.toggle('hidden', overlayMode !== 'pause');
  restartButton.classList.toggle('hidden', overlayMode !== 'pause');
  applyButton.classList.toggle('hidden', overlayMode !== 'pause');
  menuButton.classList.toggle('hidden', overlayMode === 'main' || overlayMode === 'continue');
  continueButton.classList.toggle('hidden', overlayMode !== 'continue');
  abandonButton.classList.toggle('hidden', overlayMode !== 'continue');
  syncDeveloperMenuUi();
}

function overlayCopyForMode(mode: typeof overlayMode): { title: string; subtitle: string } {
  if (mode === 'pause') {
    return {
      title: t('overlay.pause.title'),
      subtitle: t('overlay.pause.subtitle'),
    };
  }
  if (mode === 'continue') {
    if (latestRankedResult?.reason === 'continueCheckpoint') {
      return {
        title: t('overlay.continue.title'),
        subtitle: t('overlay.continue.rankedRecorded', {
          reason: engine.getGameOverReasonText(),
          count: engine.continuesRemaining,
          score: latestRankedResult.score,
          mode: t(RANKED_MODES[latestRankedResult.modeCode].labelKey),
          rank: latestRankedResult.rank,
        }),
      };
    }
    return {
      title: t('overlay.continue.title'),
      subtitle: t('overlay.continue.subtitle', {
        reason: engine.getGameOverReasonText(),
        count: engine.continuesRemaining,
      }),
    };
  }
  if (mode === 'gameOver') {
    return {
      title: t('overlay.gameOver.title'),
      subtitle: t('overlay.gameOver.subtitle', { reason: engine.getGameOverReasonText() }),
    };
  }
  return {
    title: t('app.brand'),
    subtitle: t('app.subtitle'),
  };
}

function toggleDeveloperMenu(): void {
  if (developerMenuVisible) {
    closeDeveloperMenu();
    return;
  }
  openDeveloperMenu();
}

function openDeveloperMenu(): void {
  if (engine.status !== 'menu' && engine.status !== 'gameOver') runDebugInvalidated = true;
  developerMenuReturnToPause = overlayMode === 'pause' || engine.status === 'paused';
  developerMenuResumeOnClose = false;
  if (engine.status === 'playing') {
    engine.togglePause();
    developerMenuResumeOnClose = true;
  }
  if (!overlay.classList.contains('active') || overlayMode !== 'pause') showOverlay('pause');
  developerMenuVisible = true;
  syncDeveloperMenuUi();
}

function closeDeveloperMenu(): void {
  developerMenuVisible = false;
  syncDeveloperMenuUi();
  if (developerMenuResumeOnClose && engine.status === 'paused') {
    developerMenuResumeOnClose = false;
    developerMenuReturnToPause = false;
    resumeGame();
    return;
  }
  const returnToPause = developerMenuReturnToPause;
  developerMenuResumeOnClose = false;
  developerMenuReturnToPause = false;
  if (!returnToPause) hideOverlay();
}

function hideDeveloperMenuAfterJump(): void {
  developerMenuVisible = false;
  developerMenuResumeOnClose = false;
  developerMenuReturnToPause = false;
  hideOverlay();
}

function syncDeveloperMenuUi(): void {
  devTools.classList.toggle('hidden', !(developerMenuVisible && overlayMode === 'pause'));
  devTools.classList.toggle('collapsed', developerToolsCollapsed);
  devToolsBody.classList.toggle('hidden', developerToolsCollapsed);
  devToolsToggleButton.textContent = t(developerToolsCollapsed ? 'dev.expand' : 'dev.collapse');
  if (developerMenuVisible) devLevelInput.value = String(engine.level);
  devTimeScaleSelect.value = String(developerTimeScale);
  devAiOverlayToggle.checked = developerShowAiOverlay;
  devPlayerInvincibleToggle.checked = engine.isDebugPlayerInvincible();
  devBaseInvincibleToggle.checked = engine.isDebugBaseInvincible();
  refreshAudioDebugUi();
}

function beginFireBinding(player: PlayerNumber): void {
  if (!canBindFireKey(player)) return;
  bindingTarget = player;
  ([1, 2, 3, 4] as PlayerNumber[]).forEach((entry) => fireBindButtonForPlayer(entry).classList.toggle('listening', entry === player));
  const button = fireBindButtonForPlayer(player);
  button.textContent = t('control.keyPrompt');
}

function endFireBinding(): void {
  bindingTarget = null;
  ([1, 2, 3, 4] as PlayerNumber[]).forEach((player) => fireBindButtonForPlayer(player).classList.remove('listening'));
  renderControlBindings();
}

function setFireBinding(player: PlayerNumber, code: string, label: string): void {
  if (player === 1) setFireBindingForPlayer(1, code, label);
  else if (player === 2) setFireBindingForPlayer(2, code, label);
  else if (player === 3) setFireBindingForPlayer(3, code, label);
  else setFireBindingForPlayer(4, code, label);
  saveControlBindings();
  applyInputSettings();
}

function updatePlayerControl(player: PlayerNumber): void {
  setControlModeForPlayer(player, normalizePlayerControlMode(controlSelectForPlayer(player).value, player));
  if (bindingTarget === player && !isKeyboardControlMode(controlModeForPlayer(player))) endFireBinding();
  normalizeControlSettings(player);
  saveControlBindings();
  applyInputSettings();
}

function updateKeyboardMoveScheme(player: PlayerNumber): void {
  setMoveSchemeForPlayer(player, normalizeMoveScheme(moveSchemeSelectForPlayer(player).value));
  normalizeControlSettings(player);
  saveControlBindings();
  applyInputSettings();
}

function updateGamepadSlot(player: PlayerNumber): void {
  setGamepadSlotForPlayer(player, normalizeGamepadSlot(gamepadSlotSelectForPlayer(player).value));
  normalizeControlSettings(player);
  saveControlBindings();
  applyInputSettings();
}

function applyInputSettings(): void {
  normalizeControlSettings();
  const gamepadTarget = targetForGamepads();
  const touchTarget = targetForTouchControl();
  const touchMode = modeForTouchControlTarget(touchTarget);
  const activePlayers = parsePlayerCount(playerCountSelect.value);
  input.setBindings({
    p1FireCode: fireBindings.p1FireCode,
    p2FireCode: fireBindings.p2FireCode,
    p3FireCode: fireBindings.p3FireCode,
    p4FireCode: fireBindings.p4FireCode,
    p1MoveScheme: fireBindings.p1MoveScheme,
    p2MoveScheme: fireBindings.p2MoveScheme,
    p3MoveScheme: fireBindings.p3MoveScheme,
    p4MoveScheme: fireBindings.p4MoveScheme,
    p1KeyboardEnabled: activePlayers >= 1 && isKeyboardControlMode(fireBindings.p1ControlMode),
    p2KeyboardEnabled: activePlayers >= 2 && isKeyboardControlMode(fireBindings.p2ControlMode),
    p3KeyboardEnabled: activePlayers >= 3 && isKeyboardControlMode(fireBindings.p3ControlMode),
    p4KeyboardEnabled: activePlayers >= 4 && isKeyboardControlMode(fireBindings.p4ControlMode),
    gamepadTarget,
    p1GamepadSlot: fireBindings.p1GamepadSlot,
    p2GamepadSlot: fireBindings.p2GamepadSlot,
    p3GamepadEnabled: activePlayers >= 3 && fireBindings.p3ControlMode === 'gamepad',
    p4GamepadEnabled: activePlayers >= 4 && fireBindings.p4ControlMode === 'gamepad',
    p3GamepadSlot: fireBindings.p3GamepadSlot,
    p4GamepadSlot: fireBindings.p4GamepadSlot,
    touchTarget,
  });
  if (touchMode) touchControls.setMode(touchMode);
  touchControls.setEnabled(touchTarget !== 'off', touchCapable);
  updatePlayerCountUi();
  renderControlBindings();
  refreshLeaderboardEligibility();
}

function renderControlBindings(): void {
  const activePlayers = parsePlayerCount(playerCountSelect.value);
  if (!bindingTarget) {
    ([1, 2, 3, 4] as PlayerNumber[]).forEach((player) => {
      fireBindButtonForPlayer(player).textContent = displayFireBinding(fireCodeForPlayer(player), fireLabelFallbackForPlayer(player));
    });
  }
  ([1, 2, 3, 4] as PlayerNumber[]).forEach((player) => {
    const enabled = player <= activePlayers;
    const mode = controlModeForPlayer(player);
    const showMoveScheme = enabled && isKeyboardControlMode(mode);
    const showGamepadSlot = enabled && mode === 'gamepad';
    const showFireBind = enabled && isKeyboardControlMode(mode);
    const group = playerGroupForPlayer(player);

    group.classList.toggle('hidden', !enabled && player >= 3);
    group.classList.toggle('disabled', !enabled);
    sourceLabelForPlayerElement(player).textContent = sourceLabelForPlayer(player, mode);
    fireLabelForPlayerElement(player).textContent = fireLabelForMode(player, mode);
    renderPlayerConfigSummary(groupSummaryForPlayer(player), player);
    controlSelectForPlayer(player).value = mode;
    moveSchemeSelectForPlayer(player).value = moveSchemeForPlayer(player);
    gamepadSlotSelectForPlayer(player).value = gamepadSlotForPlayer(player);
    controlHintForPlayer(player).textContent = t(enabled ? controlHintKeyForMode(mode) : 'menu.controlHintInactive');
    moveHintForPlayer(player).textContent = t(enabled ? moveSchemeHintKey(moveSchemeForPlayer(player)) : 'menu.keyboardMoveHintInactive');
    fireHintForPlayer(player).textContent = t(fireHintKeyForMode(mode, enabled));
    moveSchemeRowForPlayer(player).classList.toggle('hidden', !showMoveScheme);
    gamepadSlotRowForPlayer(player).classList.toggle('hidden', !showGamepadSlot);
    fireBindRowForPlayer(player).classList.toggle('hidden', !showFireBind);
    moveSchemeRowForPlayer(player).classList.toggle('disabled', !showMoveScheme);
    gamepadSlotRowForPlayer(player).classList.toggle('disabled', !showGamepadSlot);
    fireBindRowForPlayer(player).classList.toggle('disabled', !showFireBind);
    fireBindButtonForPlayer(player).disabled = !showFireBind;
  });
  localeSelect.value = getLocale();
  localMultiplayerNote.classList.toggle('hidden', activePlayers < 3);
  p3ControlRow.classList.toggle('hidden', activePlayers < 3);
  p4ControlRow.classList.toggle('hidden', activePlayers < 4);
}

function updatePlayerCountUi(): void {
  const activePlayers = parsePlayerCount(playerCountSelect.value);
  ([2, 3, 4] as PlayerNumber[]).forEach((player) => {
    const enabled = player <= activePlayers;
    controlSelectForPlayer(player).disabled = !enabled;
    tankClassSelectForPlayer(player).disabled = !enabled;
    controlSelectForPlayer(player).closest('.toggle-row')?.classList.toggle('disabled', !enabled);
    tankClassRowForPlayer(player).classList.toggle('hidden', !enabled && player >= 3);
    tankClassRowForPlayer(player).classList.toggle('disabled', !enabled);
    playerGroupForPlayer(player).classList.toggle('hidden', !enabled && player >= 3);
    playerGroupForPlayer(player).classList.toggle('disabled', !enabled);
  });
  localMultiplayerNote.classList.toggle('hidden', activePlayers < 3);
  if (bindingTarget && bindingTarget > activePlayers) endFireBinding();
}

function loadControlBindings(): StoredControlBindings {
  const defaults: StoredControlBindings = {
    p1FireCode: 'Space',
    p1FireLabel: 'Space',
    p2FireCode: 'KeyJ',
    p2FireLabel: 'J',
    p3FireCode: 'KeyY',
    p3FireLabel: 'Y',
    p4FireCode: 'KeyO',
    p4FireLabel: 'O',
    p1ControlMode: 'keyboard',
    p2ControlMode: 'keyboard',
    p3ControlMode: 'gamepad',
    p4ControlMode: 'gamepad',
    p1MoveScheme: 'arrows',
    p2MoveScheme: 'wasd',
    p3MoveScheme: 'tfgh',
    p4MoveScheme: 'ijkl',
    p1GamepadSlot: 'auto',
    p2GamepadSlot: 'auto',
    p3GamepadSlot: 'auto',
    p4GamepadSlot: 'auto',
  };
  try {
    const raw = localStorage.getItem('siege-tank-controls');
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as Partial<StoredControlBindings & InputBindings>;
    const migrated: StoredControlBindings = { ...defaults, ...parsed };
    if (!parsed.p1ControlMode && parsed.gamepadTarget === 'p1') migrated.p1ControlMode = 'gamepad';
    if (!parsed.p2ControlMode && parsed.gamepadTarget === 'p2') migrated.p2ControlMode = 'gamepad';
    migrated.p1ControlMode = normalizePlayerControlMode(migrated.p1ControlMode, 1);
    migrated.p2ControlMode = normalizePlayerControlMode(migrated.p2ControlMode, 2);
    migrated.p3ControlMode = normalizePlayerControlMode(migrated.p3ControlMode, 3);
    migrated.p4ControlMode = normalizePlayerControlMode(migrated.p4ControlMode, 4);
    migrated.p1MoveScheme = normalizeMoveScheme(migrated.p1MoveScheme);
    migrated.p2MoveScheme = normalizeMoveScheme(migrated.p2MoveScheme);
    migrated.p3MoveScheme = normalizeMoveScheme(migrated.p3MoveScheme);
    migrated.p4MoveScheme = normalizeMoveScheme(migrated.p4MoveScheme);
    migrated.p1GamepadSlot = normalizeGamepadSlot(migrated.p1GamepadSlot);
    migrated.p2GamepadSlot = normalizeGamepadSlot(migrated.p2GamepadSlot);
    migrated.p3GamepadSlot = normalizeGamepadSlot(migrated.p3GamepadSlot);
    migrated.p4GamepadSlot = normalizeGamepadSlot(migrated.p4GamepadSlot);
    return migrated;
  } catch {
    return defaults;
  }
}

function saveControlBindings(): void {
  try {
    localStorage.setItem('siege-tank-controls', JSON.stringify(fireBindings));
  } catch {
    // Ignore private browsing storage failures.
  }
}

function labelForKeyEvent(event: KeyboardEvent): string {
  if (event.code === 'Space') return t('control.key.space');
  if (event.key.length === 1) return event.key.toUpperCase();
  return event.key || event.code.replace(/^Key/, '').replace(/^Digit/, '');
}

function isTouchCapable(): boolean {
  return window.matchMedia('(pointer: coarse)').matches || navigator.maxTouchPoints > 0;
}

function parseLevelDelay(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(2, Math.min(10, parsed)) : 5;
}

function parseInitialPlayerLevel(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(1, Math.min(4, Math.round(parsed))) : 1;
}

function parsePlayerCount(value: string): PlayerCount {
  const parsed = Number(value);
  if (parsed >= 4) return 4;
  if (parsed >= 3) return 3;
  if (parsed >= 2) return 2;
  return 1;
}

function normalizePlayerTankClass(value: unknown): PlayerTankClass {
  return value === 'assault' || value === 'heavy' ? value : 'standard';
}

function selectedRankedMode(): RankedModeCode {
  const value = rankedPresetSelect.value as RankedModeCode;
  return value in RANKED_MODES ? value : DEFAULT_RANKED_MODE;
}

function isRankedRuleset(): boolean {
  return rulesetSelect.value !== 'custom';
}

function parseDebugLevel(value: string): number {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Math.max(1, Math.min(99, Math.round(parsed))) : Math.max(1, engine.level);
}

function parseDebugTimeScale(value: string): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 1;
  if (parsed <= 0.1) return 0.1;
  if (parsed <= 0.25) return 0.25;
  if (parsed <= 0.5) return 0.5;
  return 1;
}

function normalizeControlSettings(preferredPlayer?: PlayerNumber): void {
  const activePlayers = parsePlayerCount(playerCountSelect.value);
  ([1, 2, 3, 4] as PlayerNumber[]).forEach((player) => {
    setControlModeForPlayer(player, normalizePlayerControlMode(controlModeForPlayer(player), player));
    setMoveSchemeForPlayer(player, normalizeMoveScheme(moveSchemeForPlayer(player)));
    setGamepadSlotForPlayer(player, normalizeGamepadSlot(gamepadSlotForPlayer(player)));
  });

  for (const player of [3, 4] as PlayerNumber[]) {
    if (isTouchControlMode(controlModeForPlayer(player))) setControlModeForPlayer(player, 'keyboard');
  }

  const touchPlayers = ([1, 2] as PlayerNumber[]).filter((player) => player <= activePlayers && isTouchControlMode(controlModeForPlayer(player)));
  if (touchPlayers.length > 1) {
    const keep = preferredPlayer && touchPlayers.includes(preferredPlayer) ? preferredPlayer : touchPlayers[0];
    touchPlayers.forEach((player) => {
      if (player !== keep) setControlModeForPlayer(player, 'keyboard');
    });
  }

  const claimedMoves = new Set<KeyboardMoveScheme>();
  const keyboardPlayers = orderedPlayers(preferredPlayer).filter((player) => player <= activePlayers && isKeyboardControlMode(controlModeForPlayer(player)));
  for (const player of keyboardPlayers) {
    const scheme = moveSchemeForPlayer(player);
    if (!claimedMoves.has(scheme)) {
      claimedMoves.add(scheme);
      continue;
    }
    const replacement = KEYBOARD_MOVE_SCHEMES.find((candidate) => !claimedMoves.has(candidate));
    if (replacement) {
      setMoveSchemeForPlayer(player, replacement);
      claimedMoves.add(replacement);
    }
  }

  const activeMoveCodes = new Set<string>();
  for (const player of keyboardPlayers) {
    moveCodesForScheme(moveSchemeForPlayer(player)).forEach((code) => activeMoveCodes.add(code));
  }
  const claimedFireCodes = new Set<string>();
  for (const player of keyboardPlayers) {
    const current = fireCodeForPlayer(player);
    if (!activeMoveCodes.has(current) && !claimedFireCodes.has(current)) {
      claimedFireCodes.add(current);
      continue;
    }
    const fallback = fireKeyFallbacksForPlayer(player).find((candidate) => !activeMoveCodes.has(candidate.code) && !claimedFireCodes.has(candidate.code));
    if (!fallback) continue;
    setFireBindingForPlayer(player, fallback.code, fallback.label);
    claimedFireCodes.add(fallback.code);
  }

  const claimedSlots = new Set<GamepadSlot>();
  const gamepadPlayers = orderedPlayers(preferredPlayer).filter((player) => player <= activePlayers && controlModeForPlayer(player) === 'gamepad');
  for (const player of gamepadPlayers) {
    const slot = gamepadSlotForPlayer(player);
    if (slot === 'auto') continue;
    if (!claimedSlots.has(slot)) {
      claimedSlots.add(slot);
      continue;
    }
    setGamepadSlotForPlayer(player, 'auto');
  }

  ([1, 2, 3, 4] as PlayerNumber[]).forEach((player) => {
    controlSelectForPlayer(player).value = controlModeForPlayer(player);
    moveSchemeSelectForPlayer(player).value = moveSchemeForPlayer(player);
    gamepadSlotSelectForPlayer(player).value = gamepadSlotForPlayer(player);
  });
}

function targetForGamepads(): GamepadTarget {
  const p1 = fireBindings.p1ControlMode === 'gamepad';
  const p2 = parsePlayerCount(playerCountSelect.value) >= 2 && fireBindings.p2ControlMode === 'gamepad';
  if (p1 && p2) return 'both';
  if (p1) return 'p1';
  if (p2) return 'p2';
  return 'off';
}

function targetForTouchControl(): TouchTarget {
  const activePlayers = parsePlayerCount(playerCountSelect.value);
  if (isTouchControlMode(fireBindings.p1ControlMode)) return 'p1';
  if (activePlayers >= 2 && isTouchControlMode(fireBindings.p2ControlMode)) return 'p2';
  return 'off';
}

function modeForTouchControlTarget(target: TouchTarget): TouchControlMode | null {
  const control = target === 'p1' ? fireBindings.p1ControlMode : target === 'p2' ? fireBindings.p2ControlMode : null;
  if (control === 'touch-stick') return 'stick';
  if (control === 'touch-dpad') return 'dpad';
  return null;
}

function syncTouchModeSelection(mode: TouchControlMode): void {
  const touchTarget = targetForTouchControl();
  const value: PlayerControlMode = mode === 'stick' ? 'touch-stick' : 'touch-dpad';
  if (touchTarget === 'p1') fireBindings.p1ControlMode = value;
  if (touchTarget === 'p2') fireBindings.p2ControlMode = value;
  renderControlBindings();
}

function normalizePlayerControlMode(value: unknown, player: PlayerNumber = 1): PlayerControlMode {
  if (value === 'gamepad') return 'gamepad';
  if (player <= 2 && (value === 'touch-dpad' || value === 'touch-stick')) return value;
  return 'keyboard';
}

function normalizeMoveScheme(value: unknown): KeyboardMoveScheme {
  if (value === 'wasd' || value === 'tfgh' || value === 'ijkl' || value === 'numpad') return value;
  return 'arrows';
}

function normalizeGamepadSlot(value: unknown): GamepadSlot {
  return value === '0' || value === '1' || value === '2' || value === '3' ? value : 'auto';
}

function isTouchControlMode(value: PlayerControlMode): boolean {
  return value === 'touch-dpad' || value === 'touch-stick';
}

function isKeyboardControlMode(value: PlayerControlMode): boolean {
  return value === 'keyboard';
}

function canBindFireKey(player: PlayerNumber): boolean {
  if (player > parsePlayerCount(playerCountSelect.value)) return false;
  return isKeyboardControlMode(controlModeForPlayer(player));
}

function controlHintKeyForMode(mode: PlayerControlMode): string {
  if (mode === 'gamepad') return 'menu.controlHintGamepad';
  if (isTouchControlMode(mode)) return 'menu.controlHintTouch';
  return 'menu.controlHintKeyboard';
}

function fireHintKeyForMode(mode: PlayerControlMode, playerEnabled: boolean): string {
  if (!playerEnabled) return 'menu.fireBindHintInactive';
  if (mode === 'gamepad') return 'menu.fireBindHintGamepad';
  if (isTouchControlMode(mode)) return 'menu.fireBindHintTouch';
  return 'menu.fireBindHint';
}

function sourceLabelForPlayer(player: PlayerNumber, mode: PlayerControlMode): string {
  if (mode === 'gamepad') return gamepadSlotLabelForPlayer(player);
  if (mode === 'touch-dpad') return t('control.source.touchDpad');
  if (mode === 'touch-stick') return t('control.source.touchStick');
  return t(moveSchemeSourceKey(moveSchemeForPlayer(player)));
}

function playerConfigSummary(player: PlayerNumber): string {
  if (player > parsePlayerCount(playerCountSelect.value)) return t('menu.playerInactive');
  const mode = controlModeForPlayer(player);
  const parts = [t(controlModeLabelKey(mode))];
  if (mode === 'keyboard') {
    parts.push(t(moveSchemeLabelKey(moveSchemeForPlayer(player))));
    parts.push(displayFireBinding(fireCodeForPlayer(player), fireLabelFallbackForPlayer(player)));
  } else if (mode === 'gamepad') {
    parts.push(gamepadSlotLabelForPlayer(player));
    parts.push('A / RT');
  } else {
    parts.push(t('control.fire.touch'));
  }
  return parts.join(' · ');
}

function renderPlayerConfigSummary(target: HTMLElement, player: PlayerNumber): void {
  target.replaceChildren();
  for (const chip of playerConfigSummaryParts(player)) {
    const badge = document.createElement('span');
    badge.className = `player-config-chip${chip.muted ? ' muted' : ''}`;
    badge.textContent = chip.label;
    target.appendChild(badge);
  }
}

function playerConfigSummaryParts(player: PlayerNumber): Array<{ label: string; muted?: boolean }> {
  if (player > parsePlayerCount(playerCountSelect.value)) {
    return [{ label: t('menu.playerInactive'), muted: true }];
  }
  const mode = controlModeForPlayer(player);
  const parts: Array<{ label: string; muted?: boolean }> = [{ label: t(controlModeLabelKey(mode)) }];
  if (mode === 'keyboard') {
    parts.push({ label: t(moveSchemeLabelKey(moveSchemeForPlayer(player))) });
    parts.push({ label: displayFireBinding(fireCodeForPlayer(player), fireLabelFallbackForPlayer(player)) });
    return parts;
  }
  if (mode === 'gamepad') {
    parts.push({ label: gamepadSlotLabelForPlayer(player) });
    parts.push({ label: 'A / RT' });
    return parts;
  }
  parts.push({ label: t('control.fire.touch') });
  return parts;
}

function controlModeLabelKey(mode: PlayerControlMode): string {
  if (mode === 'gamepad') return 'menu.control.gamepad';
  if (mode === 'touch-dpad') return 'menu.control.touchDpad';
  if (mode === 'touch-stick') return 'menu.control.touchStick';
  return 'menu.control.keyboard';
}

function gamepadSlotLabelForPlayer(player: PlayerNumber): string {
  const slot = gamepadSlotForPlayer(player);
  if (slot !== 'auto') return gamepadSlotLabel(slot);
  const activePlayers = parsePlayerCount(playerCountSelect.value);
  const gamepadPlayers = ([1, 2, 3, 4] as PlayerNumber[]).filter((entry) => entry <= activePlayers && controlModeForPlayer(entry) === 'gamepad');
  const autoIndex = gamepadPlayers.indexOf(player);
  if (autoIndex === 0) return t('control.gamepad.autoOne');
  if (autoIndex === 1) return t('control.gamepad.autoTwo');
  if (autoIndex === 2) return t('control.gamepad.autoThree');
  if (autoIndex === 3) return t('control.gamepad.autoFour');
  return t('control.gamepad.auto');
}

function gamepadSlotForPlayer(player: PlayerNumber): GamepadSlot {
  if (player === 1) return fireBindings.p1GamepadSlot;
  if (player === 2) return fireBindings.p2GamepadSlot;
  if (player === 3) return fireBindings.p3GamepadSlot;
  return fireBindings.p4GamepadSlot;
}

function gamepadSlotLabel(slot: GamepadSlot): string {
  if (slot === '0') return t('control.gamepad.one');
  if (slot === '1') return t('control.gamepad.two');
  if (slot === '2') return t('control.gamepad.three');
  if (slot === '3') return t('control.gamepad.four');
  return t('control.gamepad.auto');
}

function moveSchemeSourceKey(value: KeyboardMoveScheme): string {
  if (value === 'wasd') return 'control.source.keyboardWasd';
  if (value === 'tfgh') return 'control.source.keyboardTfgh';
  if (value === 'ijkl') return 'control.source.keyboardIjkl';
  if (value === 'numpad') return 'control.source.keyboardNumpad';
  return 'control.source.keyboardArrows';
}

function moveSchemeHintKey(value: KeyboardMoveScheme): string {
  if (value === 'wasd') return 'menu.keyboardMoveHintWasd';
  if (value === 'tfgh') return 'menu.keyboardMoveHintTfgh';
  if (value === 'ijkl') return 'menu.keyboardMoveHintIjkl';
  if (value === 'numpad') return 'menu.keyboardMoveHintNumpad';
  return 'menu.keyboardMoveHintArrows';
}

function fireLabelForMode(player: PlayerNumber, mode: PlayerControlMode): string {
  if (mode === 'gamepad') return 'A / RT';
  if (isTouchControlMode(mode)) return t('control.fire.touch');
  return displayFireBinding(fireCodeForPlayer(player), fireLabelFallbackForPlayer(player));
}

function moveSchemeLabelKey(value: KeyboardMoveScheme): string {
  if (value === 'wasd') return 'menu.moveScheme.wasd';
  if (value === 'tfgh') return 'menu.moveScheme.tfgh';
  if (value === 'ijkl') return 'menu.moveScheme.ijkl';
  if (value === 'numpad') return 'menu.moveScheme.numpad';
  return 'menu.moveScheme.arrows';
}

function orderedPlayers(preferredPlayer?: PlayerNumber): PlayerNumber[] {
  const players: PlayerNumber[] = [1, 2, 3, 4];
  if (!preferredPlayer) return players;
  return [preferredPlayer, ...players.filter((player) => player !== preferredPlayer)];
}

function moveCodesForScheme(scheme: KeyboardMoveScheme): string[] {
  if (scheme === 'wasd') return ['KeyW', 'KeyA', 'KeyS', 'KeyD'];
  if (scheme === 'tfgh') return ['KeyT', 'KeyF', 'KeyG', 'KeyH'];
  if (scheme === 'ijkl') return ['KeyI', 'KeyJ', 'KeyK', 'KeyL'];
  if (scheme === 'numpad') return ['Numpad8', 'Numpad5', 'Numpad2', 'Numpad4', 'Numpad6'];
  return ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
}

function fireKeyFallbacksForPlayer(player: PlayerNumber): Array<{ code: string; label: string }> {
  if (player === 1) return [
    { code: 'Space', label: 'Space' },
    { code: 'Enter', label: 'Enter' },
    { code: 'Slash', label: '/' },
  ];
  if (player === 2) return [
    { code: 'KeyJ', label: 'J' },
    { code: 'KeyQ', label: 'Q' },
    { code: 'KeyE', label: 'E' },
    { code: 'KeyC', label: 'C' },
  ];
  if (player === 3) return [
    { code: 'KeyY', label: 'Y' },
    { code: 'KeyR', label: 'R' },
    { code: 'KeyV', label: 'V' },
    { code: 'KeyB', label: 'B' },
  ];
  return [
    { code: 'KeyO', label: 'O' },
    { code: 'KeyP', label: 'P' },
    { code: 'Numpad0', label: 'Num 0' },
    { code: 'Semicolon', label: ';' },
  ];
}

function controlModeForPlayer(player: PlayerNumber): PlayerControlMode {
  if (player === 1) return fireBindings.p1ControlMode;
  if (player === 2) return fireBindings.p2ControlMode;
  if (player === 3) return fireBindings.p3ControlMode;
  return fireBindings.p4ControlMode;
}

function setControlModeForPlayer(player: PlayerNumber, mode: PlayerControlMode): void {
  if (player === 1) fireBindings.p1ControlMode = mode;
  else if (player === 2) fireBindings.p2ControlMode = mode;
  else if (player === 3) fireBindings.p3ControlMode = mode;
  else fireBindings.p4ControlMode = mode;
}

function moveSchemeForPlayer(player: PlayerNumber): KeyboardMoveScheme {
  if (player === 1) return fireBindings.p1MoveScheme;
  if (player === 2) return fireBindings.p2MoveScheme;
  if (player === 3) return fireBindings.p3MoveScheme;
  return fireBindings.p4MoveScheme;
}

function setMoveSchemeForPlayer(player: PlayerNumber, scheme: KeyboardMoveScheme): void {
  if (player === 1) fireBindings.p1MoveScheme = scheme;
  else if (player === 2) fireBindings.p2MoveScheme = scheme;
  else if (player === 3) fireBindings.p3MoveScheme = scheme;
  else fireBindings.p4MoveScheme = scheme;
}

function setGamepadSlotForPlayer(player: PlayerNumber, slot: GamepadSlot): void {
  if (player === 1) fireBindings.p1GamepadSlot = slot;
  else if (player === 2) fireBindings.p2GamepadSlot = slot;
  else if (player === 3) fireBindings.p3GamepadSlot = slot;
  else fireBindings.p4GamepadSlot = slot;
}

function fireCodeForPlayer(player: PlayerNumber): string {
  if (player === 1) return fireBindings.p1FireCode;
  if (player === 2) return fireBindings.p2FireCode;
  if (player === 3) return fireBindings.p3FireCode;
  return fireBindings.p4FireCode;
}

function fireLabelFallbackForPlayer(player: PlayerNumber): string {
  if (player === 1) return fireBindings.p1FireLabel;
  if (player === 2) return fireBindings.p2FireLabel;
  if (player === 3) return fireBindings.p3FireLabel;
  return fireBindings.p4FireLabel;
}

function setFireBindingForPlayer(player: PlayerNumber, code: string, label: string): void {
  if (player === 1) {
    fireBindings.p1FireCode = code;
    fireBindings.p1FireLabel = label;
  } else if (player === 2) {
    fireBindings.p2FireCode = code;
    fireBindings.p2FireLabel = label;
  } else if (player === 3) {
    fireBindings.p3FireCode = code;
    fireBindings.p3FireLabel = label;
  } else {
    fireBindings.p4FireCode = code;
    fireBindings.p4FireLabel = label;
  }
}

function controlSelectForPlayer(player: PlayerNumber): HTMLSelectElement {
  if (player === 1) return p1ControlSelect;
  if (player === 2) return p2ControlSelect;
  if (player === 3) return p3ControlSelect;
  return p4ControlSelect;
}

function moveSchemeSelectForPlayer(player: PlayerNumber): HTMLSelectElement {
  if (player === 1) return p1MoveSchemeSelect;
  if (player === 2) return p2MoveSchemeSelect;
  if (player === 3) return p3MoveSchemeSelect;
  return p4MoveSchemeSelect;
}

function gamepadSlotSelectForPlayer(player: PlayerNumber): HTMLSelectElement {
  if (player === 1) return p1GamepadSlotSelect;
  if (player === 2) return p2GamepadSlotSelect;
  if (player === 3) return p3GamepadSlotSelect;
  return p4GamepadSlotSelect;
}

function fireBindButtonForPlayer(player: PlayerNumber): HTMLButtonElement {
  if (player === 1) return p1FireBindButton;
  if (player === 2) return p2FireBindButton;
  if (player === 3) return p3FireBindButton;
  return p4FireBindButton;
}

function playerGroupForPlayer(player: PlayerNumber): HTMLElement {
  if (player === 1) return p1Group;
  if (player === 2) return p2Group;
  if (player === 3) return p3Group;
  return p4Group;
}

function groupSummaryForPlayer(player: PlayerNumber): HTMLElement {
  if (player === 1) return p1GroupSummary;
  if (player === 2) return p2GroupSummary;
  if (player === 3) return p3GroupSummary;
  return p4GroupSummary;
}

function controlHintForPlayer(player: PlayerNumber): HTMLElement {
  if (player === 1) return p1ControlHint;
  if (player === 2) return p2ControlHint;
  if (player === 3) return p3ControlHint;
  return p4ControlHint;
}

function moveHintForPlayer(player: PlayerNumber): HTMLElement {
  if (player === 1) return p1MoveHint;
  if (player === 2) return p2MoveHint;
  if (player === 3) return p3MoveHint;
  return p4MoveHint;
}

function fireHintForPlayer(player: PlayerNumber): HTMLElement {
  if (player === 1) return p1FireHint;
  if (player === 2) return p2FireHint;
  if (player === 3) return p3FireHint;
  return p4FireHint;
}

function moveSchemeRowForPlayer(player: PlayerNumber): HTMLElement {
  if (player === 1) return p1MoveSchemeRow;
  if (player === 2) return p2MoveSchemeRow;
  if (player === 3) return p3MoveSchemeRow;
  return p4MoveSchemeRow;
}

function gamepadSlotRowForPlayer(player: PlayerNumber): HTMLElement {
  if (player === 1) return p1GamepadSlotRow;
  if (player === 2) return p2GamepadSlotRow;
  if (player === 3) return p3GamepadSlotRow;
  return p4GamepadSlotRow;
}

function fireBindRowForPlayer(player: PlayerNumber): HTMLElement {
  if (player === 1) return p1FireBindRow;
  if (player === 2) return p2FireBindRow;
  if (player === 3) return p3FireBindRow;
  return p4FireBindRow;
}

function sourceLabelForPlayerElement(player: PlayerNumber): HTMLElement {
  if (player === 1) return p1SourceLabel;
  if (player === 2) return p2SourceLabel;
  if (player === 3) return p3SourceLabel;
  return p4SourceLabel;
}

function fireLabelForPlayerElement(player: PlayerNumber): HTMLElement {
  if (player === 1) return p1FireLabel;
  if (player === 2) return p2FireLabel;
  if (player === 3) return p3FireLabel;
  return p4FireLabel;
}

function tankClassSelectForPlayer(player: PlayerNumber): HTMLSelectElement {
  if (player === 1) return p1TankClassSelect;
  if (player === 2) return p2TankClassSelect;
  if (player === 3) return p3TankClassSelect;
  return p4TankClassSelect;
}

function tankClassRowForPlayer(player: PlayerNumber): HTMLElement {
  if (player === 3) return p3TankClassRow;
  if (player === 4) return p4TankClassRow;
  return p2TankClassRow;
}

function displayFireBinding(code: string, fallbackLabel: string): string {
  if (code === 'Space') return t('control.key.space');
  if (/^Key[A-Z]$/.test(code)) return code.slice(3);
  if (/^Digit\d$/.test(code)) return code.slice(5);
  if (/^Numpad\d$/.test(code)) return `Num ${code.slice(6)}`;
  return fallbackLabel;
}

function applyStoredLocale(): void {
  try {
    const raw = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (raw === 'zh' || raw === 'en') {
      setLocale(raw);
      return;
    }
  } catch {
    // Ignore storage failures.
  }
  const preferred = navigator.language.toLowerCase().startsWith('en') ? 'en' : 'zh';
  setLocale(preferred);
}

function applyLocaleSelection(locale: Locale): void {
  setLocale(locale);
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // Ignore storage failures.
  }
  applyLocale();
}

function applyLocale(): void {
  document.documentElement.lang = getLocale() === 'en' ? 'en' : 'zh-CN';
  document.title = t('app.documentTitle');
  document.querySelector('meta[name=\"apple-mobile-web-app-title\"]')?.setAttribute('content', t('app.brand'));
  translateDocument(document);
  sfxTestButton.textContent = t('action.previewExplosionText');
  refreshAudioDebugUi();
  touchControls.refreshLocale();
  view.refreshLocale();
  syncFullscreenUi();
  renderRankedSummary();
  renderControlBindings();
  renderLeaderboardState();
  renderRankedIdentity();
  if (overlay.classList.contains('active')) refreshOverlayCopy();
  hud.update(engine);
  view.render(engine);
}

function currentConfiguredMode(): GameModeConfig {
  if (isRankedRuleset()) {
    return { ...RANKED_MODES[selectedRankedMode()].config };
  }
  return {
    infiniteLives: infiniteToggle.checked,
    invincibleBase: baseToggle.checked,
    friendlyFire: friendlyToggle.checked,
    upgradeDropRate: upgradeRateSelect.value as UpgradeDropRate,
    loseUpgradeOnDeath: loseUpgradeToggle.checked,
    playerCount: parsePlayerCount(playerCountSelect.value),
    levelTransitionDelay: parseLevelDelay(levelDelaySelect.value),
    obstacleDensity: obstacleDensitySelect.value as ObstacleDensity,
    initialPlayerLevel: parseInitialPlayerLevel(initialLevelSelect.value),
    enemyAiMode: enemyAiSelect.value as EnemyAiMode,
    playerTankClasses: [
      normalizePlayerTankClass(p1TankClassSelect.value),
      normalizePlayerTankClass(p2TankClassSelect.value),
      normalizePlayerTankClass(p3TankClassSelect.value),
      normalizePlayerTankClass(p4TankClassSelect.value),
    ],
  };
}

function persistLeaderboardNickname(): void {
  playerProfile = savePlayerProfile({
    ...playerProfile,
    nickname: sanitizeNickname(leaderboardNicknameInput.value),
  });
  leaderboardNicknameInput.value = playerProfile.nickname;
  renderRankedIdentity();
}

function syncRulesetUi(syncBoard = true): void {
  const ranked = isRankedRuleset();
  if (ranked) {
    applyRankedPresetToForm();
    if (syncBoard) setLeaderboardMode(selectedRankedMode(), false);
  }
  rankedSection.classList.toggle('hidden', !ranked);
  customBasicSection.classList.toggle('hidden', ranked);
  customRulesSection.classList.toggle('hidden', ranked);
  customProgressionSection.classList.toggle('hidden', ranked);
  renderRankedSummary();
  refreshLeaderboardEligibility();
  renderRankedIdentity();
}

function applyRankedPresetToForm(): void {
  const preset = RANKED_MODES[selectedRankedMode()];
  const mode = preset.config;
  playerCountSelect.value = String(mode.playerCount);
  infiniteToggle.checked = mode.infiniteLives;
  baseToggle.checked = mode.invincibleBase;
  friendlyToggle.checked = mode.friendlyFire;
  upgradeRateSelect.value = mode.upgradeDropRate;
  loseUpgradeToggle.checked = mode.loseUpgradeOnDeath;
  levelDelaySelect.value = String(mode.levelTransitionDelay);
  obstacleDensitySelect.value = mode.obstacleDensity;
  enemyAiSelect.value = mode.enemyAiMode;
  initialLevelSelect.value = String(mode.initialPlayerLevel);
  p1TankClassSelect.value = mode.playerTankClasses?.[0] ?? 'standard';
  p2TankClassSelect.value = mode.playerTankClasses?.[1] ?? 'standard';
  p3TankClassSelect.value = mode.playerTankClasses?.[2] ?? 'standard';
  p4TankClassSelect.value = mode.playerTankClasses?.[3] ?? 'standard';
}

function renderRankedSummary(): void {
  const preset = RANKED_MODES[selectedRankedMode()];
  rankedSummaryTitle.textContent = t(preset.labelKey);
  rankedSummarySubtitle.textContent = t(preset.subtitleKey);
  rankedSummaryList.replaceChildren();

  const rows: Array<[string, string]> = [
    [t('ranked.summary.board'), t(preset.labelKey)],
    [t('ranked.summary.players'), t(preset.playerCount === 1 ? 'menu.playerCount.one' : 'menu.playerCount.two')],
    [t('ranked.summary.rules'), t('ranked.summary.rulesValue')],
    [t('ranked.summary.progression'), t(`ranked.summary.progression.${preset.variant}`)],
    [t('ranked.summary.battlefield'), t(`ranked.summary.battlefield.${preset.variant}`)],
  ];

  rows.forEach(([label, value]) => {
    const row = document.createElement('div');
    row.className = 'ranked-summary-row';
    const heading = document.createElement('strong');
    heading.textContent = label;
    const content = document.createElement('span');
    content.textContent = value;
    row.append(heading, content);
    rankedSummaryList.appendChild(row);
  });
}

function refreshLeaderboardEligibility(): void {
  if (!isRankedRuleset()) {
    leaderboardEligibility.textContent = t('leaderboard.ineligible');
    renderRankedIdentity();
    return;
  }
  const rankedMode = rankedModeForConfig(currentConfiguredMode());
  if (!rankedMode) {
    leaderboardEligibility.textContent = t('leaderboard.ineligible');
    renderRankedIdentity();
    return;
  }
  leaderboardEligibility.textContent = t('leaderboard.eligiblePreset', { mode: t(RANKED_MODES[rankedMode].labelKey) });
  renderRankedIdentity();
}

function setLeaderboardMode(mode: RankedModeCode, syncRankedPreset = true): void {
  leaderboardMode = mode;
  if (syncRankedPreset && isRankedRuleset() && rankedPresetSelect.value !== mode) {
    rankedPresetSelect.value = mode;
    syncRulesetUi(false);
    applyInputSettings();
  }
  leaderboardTabs.forEach((tab) => {
    const active = tab.dataset.mode === mode;
    tab.classList.toggle('active', active);
    tab.setAttribute('aria-pressed', String(active));
  });
  renderRankedIdentity();
  void loadLeaderboard();
}

async function loadLeaderboard(forceLoading = false): Promise<void> {
  if (forceLoading || leaderboardState.status === 'unavailable') {
    leaderboardState = { status: 'loading', entries: [], myEntry: leaderboardState.myEntry };
    renderLeaderboardState();
  }
  try {
    const params = new URLSearchParams({
      mode: leaderboardMode,
      limit: '20',
      installId: playerProfile.installId,
    });
    const response = await fetch(`/api/leaderboard/list?${params.toString()}`);
    if (!response.ok) {
      leaderboardState = { status: 'unavailable', entries: [], myEntry: leaderboardState.myEntry };
      renderLeaderboardState();
      return;
    }
    const payload = await response.json() as {
      ok?: boolean;
      entries?: LeaderboardEntryRow[];
      myEntry?: PersonalLeaderboardEntry | null;
    };
    leaderboardState = {
      status: 'ready',
      entries: Array.isArray(payload.entries) ? payload.entries : [],
      myEntry: payload.myEntry ?? null,
    };
  } catch {
    leaderboardState = { status: 'unavailable', entries: [], myEntry: leaderboardState.myEntry };
  }
  renderLeaderboardState();
}

function renderLeaderboardState(): void {
  renderLeaderboardStatus();
  renderLeaderboardPersonal();
  renderRankedIdentity();
  leaderboardList.replaceChildren();
  if (leaderboardState.status === 'loading') {
    leaderboardList.appendChild(renderLeaderboardPlaceholder(t('leaderboard.loading')));
    return;
  }
  if (leaderboardState.status === 'unavailable') {
    leaderboardList.appendChild(renderLeaderboardPlaceholder(t('leaderboard.unavailable')));
    return;
  }
  if (!leaderboardState.entries.length) {
    leaderboardList.appendChild(renderLeaderboardPlaceholder(t('leaderboard.empty')));
    return;
  }

  leaderboardState.entries.forEach((entry, index) => {
    const item = document.createElement('li');
    item.className = `leaderboard-entry${entry.isMine ? ' mine' : ''}`;

    const header = document.createElement('div');
    header.className = 'leaderboard-entry-header';

    const rank = document.createElement('span');
    rank.className = 'leaderboard-entry-rank';
    rank.textContent = t('leaderboard.rank', { rank: index + 1 });

    const name = document.createElement('strong');
    name.className = 'leaderboard-entry-name';
    name.textContent = entry.nickname || 'Commander';

    header.append(rank, name);
    if (entry.isMine) {
      const badge = document.createElement('span');
      badge.className = 'leaderboard-entry-badge';
      badge.textContent = t('leaderboard.meBadge');
      header.appendChild(badge);
    }

    const scoreLine = document.createElement('div');
    scoreLine.className = 'leaderboard-entry-meta';
    scoreLine.textContent = t('leaderboard.scoreLine', {
      score: entry.score,
      stage: entry.stage,
    });

    const durationLine = document.createElement('div');
    durationLine.className = 'leaderboard-entry-meta';
    const duration = formatLeaderboardDuration(entry.durationMs);
    durationLine.textContent = t('leaderboard.durationLine', {
      minutes: duration.minutes,
      seconds: duration.seconds,
    });

    item.append(header, scoreLine, durationLine);
    leaderboardList.appendChild(item);
  });
}

function renderLeaderboardPersonal(): void {
  leaderboardPersonal.replaceChildren();

  const title = document.createElement('strong');
  title.textContent = t('leaderboard.meTitle');
  leaderboardPersonal.appendChild(title);

  if (!leaderboardState.myEntry) {
    const empty = document.createElement('small');
    empty.textContent = t('leaderboard.meEmpty');
    leaderboardPersonal.appendChild(empty);
    return;
  }

  const entry = leaderboardState.myEntry;
  const summary = document.createElement('span');
  summary.textContent = t('leaderboard.meRanked', {
    nickname: entry.nickname,
    rank: entry.rank,
    score: entry.score,
  });

  const detail = document.createElement('small');
  const duration = formatLeaderboardDuration(entry.durationMs);
  detail.textContent = t('leaderboard.meBest', {
    stage: entry.stage,
    minutes: duration.minutes,
    seconds: duration.seconds,
  });

  leaderboardPersonal.append(summary, detail);
}

function renderRankedIdentity(): void {
  rankedIdentityNickname.textContent = playerProfile.nickname;
  rankedIdentityBoard.textContent = isRankedRuleset()
    ? t('leaderboard.identityBoardActive', { mode: t(RANKED_MODES[selectedRankedMode()].labelKey) })
    : t('leaderboard.identityBoardCustom', { mode: t(RANKED_MODES[leaderboardMode].labelKey) });

  if (leaderboardState.status === 'loading') {
    rankedIdentityStandingTitle.textContent = t('leaderboard.identityLoadingTitle');
    rankedIdentityStandingDetail.textContent = t('leaderboard.identityLoadingDetail');
    return;
  }
  if (leaderboardState.status === 'unavailable') {
    rankedIdentityStandingTitle.textContent = t('leaderboard.identityUnavailableTitle');
    rankedIdentityStandingDetail.textContent = t('leaderboard.identityUnavailableDetail');
    return;
  }
  if (!leaderboardState.myEntry) {
    rankedIdentityStandingTitle.textContent = t('leaderboard.identityEmptyTitle');
    rankedIdentityStandingDetail.textContent = t('leaderboard.identityEmptyDetail');
    return;
  }

  const entry = leaderboardState.myEntry;
  const duration = formatLeaderboardDuration(entry.durationMs);
  rankedIdentityStandingTitle.textContent = t('leaderboard.identityRankedTitle', {
    rank: entry.rank,
    score: entry.score,
  });
  rankedIdentityStandingDetail.textContent = t('leaderboard.identityRankedDetail', {
    mode: t(RANKED_MODES[leaderboardMode].labelKey),
    stage: entry.stage,
    minutes: duration.minutes,
    seconds: duration.seconds,
  });
}

function renderLeaderboardStatus(): void {
  leaderboardStatus.replaceChildren();
  leaderboardStatusBadges.replaceChildren();
  leaderboardStatus.classList.toggle('hidden', !latestRankedResult);
  leaderboardStatusBadges.classList.toggle('hidden', !latestRankedResult);
  if (!latestRankedResult) return;

  const title = document.createElement('strong');
  title.textContent = t('leaderboard.statusTitle');

  const summary = document.createElement('span');
  summary.textContent = t('leaderboard.statusRanked', {
    mode: t(RANKED_MODES[latestRankedResult.modeCode].labelKey),
    score: latestRankedResult.score,
    rank: latestRankedResult.rank,
  });

  const best = document.createElement('small');
  best.textContent = t('leaderboard.statusBest', {
    bestScore: latestRankedResult.bestScore,
    bestStage: latestRankedResult.bestStage,
  });

  leaderboardStatus.append(title, summary, best);
  if (latestRankedResult.personalBestImproved) {
    const badge = document.createElement('small');
    badge.textContent = t('leaderboard.statusBestImproved');
    leaderboardStatus.appendChild(badge);
  }
  renderLeaderboardMilestones(latestRankedResult);
}

function renderLeaderboardMilestones(result: RankedSubmissionFeedback): void {
  const milestones = milestoneLabelsForResult(result);
  leaderboardStatusBadges.classList.toggle('hidden', milestones.length === 0);
  milestones.forEach(({ key, emphasis }) => {
    const badge = document.createElement('span');
    badge.className = `leaderboard-status-badge${emphasis ? ' emphasis' : ''}`;
    badge.textContent = t(key);
    leaderboardStatusBadges.appendChild(badge);
  });
}

function milestoneLabelsForResult(result: RankedSubmissionFeedback): Array<{ key: string; emphasis?: boolean }> {
  const milestones: Array<{ key: string; emphasis?: boolean }> = [];
  if (result.personalBestImproved) {
    milestones.push({ key: 'leaderboard.statusMilestone.personalBest' });
  }
  if ((result.previousRank == null || result.previousRank > 10) && result.rank <= 10) {
    milestones.push({ key: 'leaderboard.statusMilestone.top10' });
  }
  if ((result.previousRank == null || result.previousRank > 3) && result.rank <= 3) {
    milestones.push({ key: 'leaderboard.statusMilestone.top3', emphasis: true });
  }
  if ((result.previousRank == null || result.previousRank > 1) && result.rank === 1) {
    milestones.push({ key: 'leaderboard.statusMilestone.champion', emphasis: true });
  }
  return milestones;
}

function celebrationTitleKeyForResult(result: RankedSubmissionFeedback): string | null {
  if ((result.previousRank == null || result.previousRank > 1) && result.rank === 1) {
    return 'leaderboard.statusMilestone.champion';
  }
  if ((result.previousRank == null || result.previousRank > 3) && result.rank <= 3) {
    return 'leaderboard.statusMilestone.top3';
  }
  if ((result.previousRank == null || result.previousRank > 10) && result.rank <= 10) {
    return 'leaderboard.statusMilestone.top10';
  }
  if (result.personalBestImproved) {
    return 'leaderboard.statusMilestone.personalBest';
  }
  return null;
}

function celebrationTierForResult(result: RankedSubmissionFeedback): 'champion' | 'elite' | 'ranked' {
  if ((result.previousRank == null || result.previousRank > 1) && result.rank === 1) {
    return 'champion';
  }
  if ((result.previousRank == null || result.previousRank > 3) && result.rank <= 3) {
    return 'elite';
  }
  return 'ranked';
}

function renderLeaderboardPlaceholder(text: string): HTMLLIElement {
  const item = document.createElement('li');
  item.className = 'leaderboard-empty';
  item.textContent = text;
  return item;
}

function formatLeaderboardDuration(durationMs: number): { minutes: string; seconds: string } {
  const totalSeconds = Math.max(0, Math.round(durationMs / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return {
    minutes: String(minutes),
    seconds: String(seconds).padStart(2, '0'),
  };
}

async function maybeSubmitLeaderboard(reason: RankedSnapshotReason): Promise<void> {
  if (runStartedAt <= 0 || leaderboardSubmissionAttempted) return;

  const snapshot = pendingRankedSnapshot ?? createRankedSnapshot(reason);
  if (!snapshot) {
    leaderboardSubmissionAttempted = true;
    showToast(t('leaderboard.submitSkipped'));
    return;
  }

  pendingRankedSnapshot = snapshot;
  const result = await submitRankedSnapshot(snapshot);
  if (!result) return;

  latestRankedResult = result;
  pendingRankedSnapshot = null;
  leaderboardSubmissionAttempted = true;
  renderLeaderboardStatus();
  showRankedCelebration(result);
  showToast(t('leaderboard.submitSuccessDetailed', {
    mode: t(RANKED_MODES[result.modeCode].labelKey),
    score: result.score,
    rank: result.rank,
  }));
  if (leaderboardMode === result.modeCode) {
    void loadLeaderboard(true);
  }
}

function createRankedSnapshot(reason: RankedSnapshotReason): RankedRunSnapshot | null {
  const modeCode = rankedModeForConfig(engine.mode);
  if (!modeCode || runContinueUsed || runDebugInvalidated) return null;
  persistLeaderboardNickname();
  const stats = engine.getStats();
  return {
    modeCode,
    score: stats.score,
    stage: stats.level,
    durationMs: Date.now() - runStartedAt,
    reason,
    playerCount: stats.playerCount,
    baseState: stats.baseState,
  };
}

async function submitRankedSnapshot(snapshot: RankedRunSnapshot): Promise<RankedSubmissionFeedback | null> {
  try {
    const response = await fetch('/api/leaderboard/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        installId: playerProfile.installId,
        nickname: playerProfile.nickname,
        modeCode: snapshot.modeCode,
        score: snapshot.score,
        stage: snapshot.stage,
        durationMs: snapshot.durationMs,
        buildVersion: BUILD_VERSION,
        summary: {
          reason: snapshot.reason,
          baseState: snapshot.baseState,
          playerCount: snapshot.playerCount,
        },
      }),
    });
    if (!response.ok) throw new Error('submit failed');
    const payload = await response.json() as RankedSubmissionFeedbackResponse;
    return {
      modeCode: snapshot.modeCode,
      score: snapshot.score,
      stage: snapshot.stage,
      durationMs: snapshot.durationMs,
      rank: payload.rank,
      previousRank: payload.previousRank,
      bestScore: payload.bestScore,
      bestStage: payload.bestStage,
      bestDurationMs: payload.bestDurationMs,
      personalBestImproved: payload.personalBestImproved,
      acceptedAsBest: payload.acceptedAsBest,
      reason: snapshot.reason,
    };
  } catch {
    showToast(t('leaderboard.submitFailed'));
    return null;
  }
}

async function continueRankedPractice(): Promise<void> {
  await maybeSubmitLeaderboard('continueCheckpoint');
  if (engine.continueCurrentLevel()) {
    runContinueUsed = true;
    showToast(t('leaderboard.practiceMode'));
    hideOverlay();
  }
}

function installMobileGestureGuards(): void {
  let lastTouchEndAt = 0;
  const syncFullscreenClass = () => syncFullscreenUi();
  const shouldBlockGesture = (target: EventTarget | null) => {
    if (target instanceof Element && target.closest('.menu-panel')) return false;
    if (document.fullscreenElement) return true;
    return target instanceof Element && Boolean(target.closest('#touch-controls, #game-root'));
  };
  const preventIfNeeded = (event: Event) => {
    if (shouldBlockGesture(event.target)) event.preventDefault();
  };

  document.addEventListener('fullscreenchange', syncFullscreenClass);
  syncFullscreenClass();
  document.addEventListener(
    'touchstart',
    (event) => {
      if (event.touches.length > 1 && shouldBlockGesture(event.target)) event.preventDefault();
    },
    { passive: false },
  );
  document.addEventListener('touchmove', preventIfNeeded, { passive: false });
  document.addEventListener(
    'touchend',
    (event) => {
      const now = Date.now();
      if (now - lastTouchEndAt < 320 && shouldBlockGesture(event.target)) event.preventDefault();
      lastTouchEndAt = now;
    },
    { passive: false },
  );
  document.addEventListener('gesturestart', preventIfNeeded, { passive: false });
  document.addEventListener('gesturechange', preventIfNeeded, { passive: false });
}

function installAudioWakeGuards(): void {
  const wakeAudio = () => {
    void audio.resume().finally(() => refreshAudioDebugUi());
  };
  const wakeIfVisible = () => {
    if (document.visibilityState === 'visible') {
      void audio.resume().finally(() => refreshAudioDebugUi());
      return;
    }
    refreshAudioDebugUi();
  };
  window.addEventListener('pointerdown', wakeAudio, { capture: true, passive: true });
  window.addEventListener('touchstart', wakeAudio, { capture: true, passive: true });
  window.addEventListener('touchend', wakeAudio, { capture: true, passive: true });
  window.addEventListener('keydown', wakeAudio, { capture: true });
  window.addEventListener('focus', wakeAudio);
  window.addEventListener('pageshow', wakeAudio);
  document.addEventListener('visibilitychange', wakeIfVisible);
}

function refreshAudioDebugUi(): void {
  devAudioState.textContent = audioStateLabel();
}

function audioStateLabel(): string {
  const rawState = audio.getState();
  return t(`dev.audio.${rawState}`);
}

function isDeveloperShortcut(event: KeyboardEvent): boolean {
  return event.code === 'F12' || (event.shiftKey && event.code === 'Backquote');
}

type PlayerControlMode = 'keyboard' | 'gamepad' | 'touch-dpad' | 'touch-stick';

interface StoredControlBindings {
  p1FireCode: string;
  p2FireCode: string;
  p3FireCode: string;
  p4FireCode: string;
  p1FireLabel: string;
  p2FireLabel: string;
  p3FireLabel: string;
  p4FireLabel: string;
  p1ControlMode: PlayerControlMode;
  p2ControlMode: PlayerControlMode;
  p3ControlMode: PlayerControlMode;
  p4ControlMode: PlayerControlMode;
  p1MoveScheme: KeyboardMoveScheme;
  p2MoveScheme: KeyboardMoveScheme;
  p3MoveScheme: KeyboardMoveScheme;
  p4MoveScheme: KeyboardMoveScheme;
  p1GamepadSlot: GamepadSlot;
  p2GamepadSlot: GamepadSlot;
  p3GamepadSlot: GamepadSlot;
  p4GamepadSlot: GamepadSlot;
}

interface LeaderboardEntryRow {
  nickname: string;
  score: number;
  stage: number;
  durationMs: number;
  submittedAt: string;
  isMine?: boolean;
}

interface LeaderboardViewState {
  status: 'loading' | 'ready' | 'unavailable';
  entries: LeaderboardEntryRow[];
  myEntry: PersonalLeaderboardEntry | null;
}

type RankedSnapshotReason = 'continueCheckpoint' | 'gameOver' | 'abandon';

interface RankedRunSnapshot {
  modeCode: RankedModeCode;
  score: number;
  stage: number;
  durationMs: number;
  playerCount: PlayerCount;
  baseState: string;
  reason: RankedSnapshotReason;
}

interface RankedSubmissionFeedbackResponse {
  rank: number;
  previousRank: number | null;
  bestScore: number;
  bestStage: number;
  bestDurationMs: number;
  personalBestImproved: boolean;
  acceptedAsBest: boolean;
}

interface RankedSubmissionFeedback extends RankedSubmissionFeedbackResponse {
  modeCode: RankedModeCode;
  score: number;
  stage: number;
  durationMs: number;
  reason: RankedSnapshotReason;
}

interface PersonalLeaderboardEntry {
  nickname: string;
  score: number;
  stage: number;
  durationMs: number;
  rank: number;
}

function required(id: string): HTMLElement {
  const element = document.getElementById(id);
  if (!element) throw new Error(`Missing element #${id}`);
  return element;
}

function requiredButton(id: string): HTMLButtonElement {
  const element = required(id);
  if (!(element instanceof HTMLButtonElement)) throw new Error(`#${id} is not a button`);
  return element;
}

function requiredInput(id: string): HTMLInputElement {
  const element = required(id);
  if (!(element instanceof HTMLInputElement)) throw new Error(`#${id} is not an input`);
  return element;
}

function requiredSelect(id: string): HTMLSelectElement {
  const element = required(id);
  if (!(element instanceof HTMLSelectElement)) throw new Error(`#${id} is not a select`);
  return element;
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement) return !target.disabled && !target.readOnly;
  if (target instanceof HTMLSelectElement) return !target.disabled;
  return target.isContentEditable || Boolean(target.closest('[contenteditable="true"]'));
}
